import React, { createContext, useState, memo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserFormType } from '../views/creatorOnBoarding/signup.types';
import { useQueryClient } from '@tanstack/react-query';
import { getProfileByUserId, getProfileByUsername } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

interface ProfileContextType {
  creatorProfileData: UserFormType | null;
  fetchProfileDataFromUserName: (
    userName: string,
    authUserId: string,
    getDetails?: boolean,
    onNotFound?: () => void,
  ) => Promise<UserFormType | null>; // Returns promise with actual data
  fetchProfileDataFromUserId: (
    userId: string,
    authUserId: string,
    getDetails?: boolean,
    onNotFound?: () => void,
  ) => Promise<UserFormType | null>; // Returns promise with actual data
  isLoadingProfile: boolean;
  forceRefetch: () => Promise<void>;
}

export const ProfileContext = createContext<ProfileContextType>({
  creatorProfileData: null,
  fetchProfileDataFromUserName: async () => null,
  fetchProfileDataFromUserId: async () => null,
  isLoadingProfile: false,
  forceRefetch: async () => {},
});

export const ProfileProvider = ({ children}: { children: React.ReactNode;}) => {
  const [creatorProfileData, setCreatorProfileData] = useState<UserFormType | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [currentFetchKey, setCurrentFetchKey] = useState<string | null>(null);
  // Add state to track active fetches
  const [activeFetches, setActiveFetches] = useState<Set<string>>(new Set());

  const { authUser } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if ((authUser?.uid && authUser?.uid === creatorProfileData?.userId)
      || (creatorProfileData && !creatorProfileData.userId)) {
      forceRefetch();
    }
  }, [authUser]) //eslint-disable-line react-hooks/exhaustive-deps

  const processProfileData = async (params: {
    userName?: string,
    userId?: string,
    authUserId?: string,
    getDetails: boolean,
    onNotFound?: () => void,
  }): Promise<UserFormType | null> => {
    if (!params || (!params.userName && !params.userId)) {
      if (params?.onNotFound) {
        params.onNotFound();
      }
      return null;
    }

    const isFirstLogin = authUser?.metadata?.creationTime === authUser?.metadata?.lastSignInTime;
    const shouldShowToast = !isFirstLogin;

    try {
      if (enableDevTools) console.log('Fetching profile data with params:', params);
      const resp = await (params.userId
        ? getProfileByUserId(params.authUserId, params.userId, params.getDetails, false)
        : getProfileByUsername(params.authUserId, params.userName, params.getDetails, false)
      );

      if (!resp.ok) {
        if (shouldShowToast) {
          toast.error('User Not Found');
        }
        if (params.onNotFound) {
          params.onNotFound();
        }
        return null;
      }

      const data: UserFormType = await resp.json();

      if (!data || !data.userName) {
        if (shouldShowToast) {
          toast.error('User Not Found');
        }
        if (params.onNotFound) {
          params.onNotFound();
        }
        return null;
      }

      data.normalizedUserName = data.userName.toLowerCase();
      return data;
    } catch (error) {
      if (shouldShowToast) {
        toast.error('User Not Found');
      }
      if (params.onNotFound) {
        params.onNotFound();
      }
      return null;
    }
  };

  // Fetch profile data using React Query's fetchQuery for caching benefits
  const fetchProfileDataFromUserName = async (
    userName: string,
    authUserId: string,
    getDetails: boolean = false,
    onNotFound?: () => void,
  ): Promise<UserFormType | null> => {
    const fetchKey = `username-${userName}-${getDetails}`;

    // Prevent duplicate fetches
    if (activeFetches.has(fetchKey)) {
      if (enableDevTools) console.log('Fetch already in progress for', fetchKey);
      return creatorProfileData; // Return current data if available
    }

    // Add to active fetches
    setActiveFetches(prev => new Set(prev).add(fetchKey));
    setCurrentFetchKey(fetchKey);
    setIsLoadingProfile(true);

    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['profile-data', 'username', userName, getDetails],
        queryFn: () => processProfileData({
          userName,
          authUserId,
          getDetails,
          onNotFound,
        }),
        staleTime: 1000*60*60*24, // 1 day
      });

      if (enableDevTools) console.log('Fetched Profile Data:', result);
      setCreatorProfileData(result);
      return result;
    } catch (error) {
      // Handle CancelledError specifically
      if (error?.name === 'CancelledError' ||
          error?.constructor?.name === 'CancelledError' ||
          error?.message?.includes('cancelled')) {
        if (enableDevTools) console.log('Query was cancelled - this is normal behavior, not calling onNotFound');
        return null;
      }

      console.error('Error fetching profile by username:', error);
      if (onNotFound) {
        onNotFound();
      }
      return null;
    } finally {
      // Remove from active fetches
      setActiveFetches(prev => {
        const newSet = new Set(prev);
        newSet.delete(fetchKey);
        return newSet;
      });
      setIsLoadingProfile(false);
    }
  };

  // Fetch profile data by userId
  const fetchProfileDataFromUserId = async (
    userId: string,
    authUserId: string,
    getDetails: boolean = false,
    onNotFound?: () => void,
  ): Promise<UserFormType | null> => {
    const fetchKey = `userId-${userId}-${getDetails}`;

    // Prevent duplicate fetches
    if (activeFetches.has(fetchKey)) {
      if (enableDevTools) console.log('Fetch already in progress for', fetchKey);
      return creatorProfileData; // Return current data if available
    }

    // Add to active fetches
    setActiveFetches(prev => new Set(prev).add(fetchKey));
    setCurrentFetchKey(fetchKey);
    setIsLoadingProfile(true);

    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['profile-data', 'userId', userId, getDetails],
        queryFn: () => processProfileData({
          userId,
          authUserId,
          getDetails,
          onNotFound,
        }),
        staleTime: 1000*60*60*24, // 1 day
      });

      if (enableDevTools) console.log('Fetched Profile Data:', result);
      setCreatorProfileData(result);
      return result;
    } catch (error) {
      // Handle CancelledError specifically
      if (error?.name === 'CancelledError' ||
          error?.constructor?.name === 'CancelledError' ||
          error?.message?.includes('cancelled') ||
          error?.toString?.().includes('CancelledError')) {
        if (enableDevTools) console.log('Query was cancelled - this is normal behavior, not calling onNotFound');
        return null;
      }

      if (enableDevTools) console.error('Error fetching profile by userId:', error);
      if (onNotFound) {
        onNotFound();
      }
      return null;
    } finally {
      // Remove from active fetches
      setActiveFetches(prev => {
        const newSet = new Set(prev);
        newSet.delete(fetchKey);
        return newSet;
      });
      setIsLoadingProfile(false);
    }
  };

  const forceRefetch = async () => {
    if (creatorProfileData) {
      // Clear any active fetches before refetching
      setActiveFetches(new Set());

      await queryClient.invalidateQueries({
        queryKey: ['profile-data'],
        refetchType: 'all'
      });

      // Re-fetch the current profile
      if (creatorProfileData.userName) {
        await fetchProfileDataFromUserName(
          creatorProfileData.userName,
          authUser?.uid,
          true
        );
      }
    }
  }

  useEffect(() => {
    if (enableDevTools) {
      console.log('Creator Profile Data: ', creatorProfileData);
      console.log('Active Fetches: ', Array.from(activeFetches));
      // console.log('Is Loading Profile: ', isLoadingProfile);
      // console.log('Current Fetch Key: ', currentFetchKey);
    }
  }, [
    creatorProfileData,
    activeFetches,
    // isLoadingProfile,
    // currentFetchKey
  ])

  return (
    <ProfileContext.Provider
      value={{
        creatorProfileData,
        fetchProfileDataFromUserName,
        fetchProfileDataFromUserId,
        isLoadingProfile,
        forceRefetch
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default memo(ProfileProvider);
